import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../common/block-text/block-text.component';
import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { InlineEditV2Directive } from '../../base-template-editor-v2/inline-edit/inline-edit-v2.directive';
import { InlineMediaV2Directive } from '../../base-template-editor-v2/inline-edit/inline-media-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import {
  X2ExperiencesInitModel,
  X2ExperiencesInitVariables,
  X2ExperiencesModel,
  X2ExperiencesVariables,
} from '../../../../models/email-template/x2-experiences.interface';

/**
 * Mockup-styled Inspiration + Big Headline + 2x Photo block - a drop-in
 * replacement for app-x2-experiences in block-renderer-v2 (same
 * [block]/[isPreview] inputs and outputs), so it covers both the
 * center-canvas summary (isPreview=true) and the right-panel edit
 * form (isPreview=false). Reuses the real, unmodified app-block-text (image
 * upload/segment/validation), app-field-editor-v2 and app-pin sub-components
 * and the exact same X2ExperiencesVariables field names - only the
 * surrounding layout is new. Unlike the Property USP variants, each card's
 * own CTA Link/CTA Text ARE editable here (matching v1).
 */
@Component({
  selector: 'app-x2-experiences-v2',
  imports: [CommonModule, ReactiveFormsModule, BlockTextComponent, FieldEditorV2Component, InlineEditV2Directive, InlineMediaV2Directive],
  templateUrl: './x2-experiences-v2.component.html',
  styleUrl: './x2-experiences-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class X2ExperiencesComponentV2 extends BaseComponent implements OnInit {
  @Input() block: X2ExperiencesModel = new X2ExperiencesInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new X2ExperiencesInitVariables();
    }
  }

  paragraphPlaceholder = 'Enter Paragraph';
  image1Placeholder = 'First Image';
  image2Placeholder = 'Second Image';
  image1HrefPlaceholder = 'Enter First Image Redirect Link';
  image2HrefPlaceholder = 'Enter Second Image Redirect Link';
  image1AltPlaceholder = 'Enter First Image Alt Text';
  image2AltPlaceholder = 'Enter Second Image Alt Text';
  heading1Placeholder = 'Enter First Heading';
  heading2Placeholder = 'Enter Second Heading';
  description1Placeholder = 'Enter First Description';
  description2Placeholder = 'Enter Second Description';
  ctaLink1Placeholder = 'Enter First CTA Link';
  ctaLink2Placeholder = 'Enter Second CTA Link';
  ctaText1Placeholder = 'Enter First CTA Text';
  ctaText2Placeholder = 'Enter Second CTA Text';

  protected readonly ctaButtonPlaceholder = 'Insert CTA text';

  protected image1AsUpload = true;
  protected image2AsUpload = true;

  protected toggleImage1Mode(): void {
    this.image1AsUpload = !this.image1AsUpload;
  }

  protected toggleImage2Mode(): void {
    this.image2AsUpload = !this.image2AsUpload;
  }

  private hrefFor(link?: string, segment?: string): string {
    const base = this.helper.getFullUrl(link);
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  get card1ImageHref(): string {
    return this.hrefFor(this.block.variables?.['X2Exp1ImageHref'], this.block.variables?.['X2Exp1ImageHrefSegmentRegex']);
  }

  get card2ImageHref(): string {
    return this.hrefFor(this.block.variables?.['X2Exp2ImageHref'], this.block.variables?.['X2Exp2ImageHrefSegmentRegex']);
  }

  get card1CtaHref(): string {
    return this.hrefFor(this.block.variables?.['X2Exp1CtaLink'], this.block.variables?.['X2Exp1CtaLinkSegmentRegex']);
  }

  get card2CtaHref(): string {
    return this.hrefFor(this.block.variables?.['X2Exp2CtaLink'], this.block.variables?.['X2Exp2CtaLinkSegmentRegex']);
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof X2ExperiencesVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
